import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../config/api';
import {
  Cog6ToothIcon,
  ClockIcon,
  CurrencyPoundIcon,
  SparklesIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface PreferencesWizardProps {
  initialPreferences?: any;
  onSave?: () => void;
  onClose?: () => void;
  coachMode?: boolean;
  clientId?: number;
}

const CUISINE_OPTIONS = [
  { value: 'mediterranean', label: 'Mediterranean', emoji: '🫒' },
  { value: 'asian', label: 'Asian', emoji: '🥢' },
  { value: 'mexican', label: 'Mexican', emoji: '🌮' },
  { value: 'indian', label: 'Indian', emoji: '🍛' },
  { value: 'italian', label: 'Italian', emoji: '🍝' },
  { value: 'american', label: 'American', emoji: '🍔' },
  { value: 'middle_eastern', label: 'Middle Eastern', emoji: '🧆' },
  { value: 'japanese', label: 'Japanese', emoji: '🍱' },
  { value: 'thai', label: 'Thai', emoji: '🍜' },
  { value: 'french', label: 'French', emoji: '🥐' },
  { value: 'british', label: 'British', emoji: '🫖' },
  { value: 'vegetarian', label: 'Vegetarian', emoji: '🥗' },
];

export const PreferencesWizard: React.FC<PreferencesWizardProps> = ({
  initialPreferences,
  onSave,
  onClose,
  coachMode,
  clientId,
}) => {
  const [preferences, setPreferences] = useState({
    preferred_cuisines: [] as string[],
    excluded_cuisines: [] as string[],
    max_prep_time_minutes: 30,
    max_cook_time_minutes: 45,
    cooking_skill_level: 'intermediate' as 'beginner' | 'intermediate' | 'advanced',
    meals_per_day: 3,
    include_snacks: true,
    batch_cooking_preference: false,
    budget_level: 'moderate' as 'economy' | 'moderate' | 'premium',
    seasonal_preference: true,
    variety_preference: 'medium' as 'low' | 'medium' | 'high',
    leftover_tolerance: 'next_day' as 'none' | 'next_day' | 'multi_day',
  });

  const [activeSection, setActiveSection] = useState<string>('cuisines');
  const [isSaved, setIsSaved] = useState(false);

  // Load initial preferences
  useEffect(() => {
    if (initialPreferences) {
      setPreferences(prev => ({
        ...prev,
        ...initialPreferences,
        preferred_cuisines: initialPreferences.preferred_cuisines || [],
        excluded_cuisines: initialPreferences.excluded_cuisines || [],
      }));
    }
  }, [initialPreferences]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => apiClient.updateMealPlanPreferences(preferences, coachMode ? clientId : undefined),
    onSuccess: () => {
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2000);
      onSave?.();
    },
  });

  const toggleCuisine = (cuisine: string, type: 'preferred' | 'excluded') => {
    setPreferences(prev => {
      const field = type === 'preferred' ? 'preferred_cuisines' : 'excluded_cuisines';
      const otherField = type === 'preferred' ? 'excluded_cuisines' : 'preferred_cuisines';
      const current = prev[field];

      // If adding to one list, remove from the other
      const newOther = prev[otherField].filter(c => c !== cuisine);

      if (current.includes(cuisine)) {
        return {
          ...prev,
          [field]: current.filter(c => c !== cuisine),
          [otherField]: newOther,
        };
      } else {
        return {
          ...prev,
          [field]: [...current, cuisine],
          [otherField]: newOther,
        };
      }
    });
  };

  const sections = [
    { id: 'cuisines', label: 'Cuisines', icon: '🍽️' },
    { id: 'timing', label: 'Time & Meals', icon: '⏱️' },
    { id: 'cooking', label: 'Cooking Style', icon: '👨‍🍳' },
    { id: 'variety', label: 'Variety & Budget', icon: '🎯' },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-700 px-6 py-4 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cog6ToothIcon className="h-6 w-6 text-white" />
            <h2 className="text-lg font-semibold text-white">Meal Planning Preferences</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        <p className="mt-1 text-sm text-blue-100">
          Customize how your meal plans are generated
        </p>
      </div>

      {/* Section Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex px-4">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeSection === section.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="mr-1">{section.icon}</span>
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Cuisines Section */}
        {activeSection === 'cuisines' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Preferred Cuisines
                <span className="ml-2 text-xs text-gray-500 font-normal">
                  (Select cuisines you enjoy)
                </span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {CUISINE_OPTIONS.map(cuisine => {
                  const isPreferred = preferences.preferred_cuisines.includes(cuisine.value);
                  const isExcluded = preferences.excluded_cuisines.includes(cuisine.value);

                  return (
                    <button
                      key={cuisine.value}
                      onClick={() => toggleCuisine(cuisine.value, 'preferred')}
                      className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                        isPreferred
                          ? 'bg-green-50 border-green-300 text-green-700'
                          : isExcluded
                          ? 'bg-gray-50 border-gray-200 text-gray-400'
                          : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                      }`}
                    >
                      <span className="mr-1">{cuisine.emoji}</span>
                      {cuisine.label}
                      {isPreferred && <CheckIcon className="h-4 w-4 inline ml-1" />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Excluded Cuisines
                <span className="ml-2 text-xs text-gray-500 font-normal">
                  (Select cuisines to avoid)
                </span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {CUISINE_OPTIONS.map(cuisine => {
                  const isExcluded = preferences.excluded_cuisines.includes(cuisine.value);
                  const isPreferred = preferences.preferred_cuisines.includes(cuisine.value);

                  return (
                    <button
                      key={cuisine.value}
                      onClick={() => toggleCuisine(cuisine.value, 'excluded')}
                      className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                        isExcluded
                          ? 'bg-red-50 border-red-300 text-red-700'
                          : isPreferred
                          ? 'bg-gray-50 border-gray-200 text-gray-400'
                          : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                      }`}
                    >
                      <span className="mr-1">{cuisine.emoji}</span>
                      {cuisine.label}
                      {isExcluded && <XMarkIcon className="h-4 w-4 inline ml-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Timing Section */}
        {activeSection === 'timing' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  <ClockIcon className="h-4 w-4 inline mr-1" />
                  Max Prep Time
                </label>
                <select
                  value={preferences.max_prep_time_minutes}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    max_prep_time_minutes: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={20}>20 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>No limit</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  <ClockIcon className="h-4 w-4 inline mr-1" />
                  Max Cook Time
                </label>
                <select
                  value={preferences.max_cook_time_minutes}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    max_cook_time_minutes: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>No limit</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Meals Per Day
                </label>
                <select
                  value={preferences.meals_per_day}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    meals_per_day: parseInt(e.target.value)
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={2}>2 meals</option>
                  <option value={3}>3 meals</option>
                  <option value={4}>4 meals</option>
                  <option value={5}>5 meals</option>
                  <option value={6}>6 meals</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Include Snacks
                </label>
                <div className="flex items-center gap-4 mt-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={preferences.include_snacks}
                      onChange={() => setPreferences(prev => ({ ...prev, include_snacks: true }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!preferences.include_snacks}
                      onChange={() => setPreferences(prev => ({ ...prev, include_snacks: false }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cooking Style Section */}
        {activeSection === 'cooking' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Cooking Skill Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['beginner', 'intermediate', 'advanced'] as const).map(level => (
                  <button
                    key={level}
                    onClick={() => setPreferences(prev => ({ ...prev, cooking_skill_level: level }))}
                    className={`px-4 py-3 rounded-lg border text-sm transition-all ${
                      preferences.cooking_skill_level === level
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-200 hover:border-blue-200'
                    }`}
                  >
                    <div className="font-medium capitalize">{level}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {level === 'beginner' && 'Simple recipes, basic techniques'}
                      {level === 'intermediate' && 'Some cooking experience'}
                      {level === 'advanced' && 'Complex recipes welcome'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.batch_cooking_preference}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    batch_cooking_preference: e.target.checked
                  }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">
                  Optimize for batch cooking / meal prep
                </span>
              </label>
              <p className="ml-6 text-xs text-gray-500 mt-1">
                Plans will include recipes that work well when prepared in advance
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Leftover Tolerance
              </label>
              <div className="grid grid-cols-3 gap-3">
                {([
                  { value: 'none', label: 'No leftovers', desc: 'Fresh meals only' },
                  { value: 'next_day', label: 'Next day', desc: 'Use tomorrow' },
                  { value: 'multi_day', label: 'Multi-day', desc: 'Batch cooking OK' },
                ] as const).map(option => (
                  <button
                    key={option.value}
                    onClick={() => setPreferences(prev => ({ ...prev, leftover_tolerance: option.value }))}
                    className={`px-4 py-3 rounded-lg border text-sm transition-all ${
                      preferences.leftover_tolerance === option.value
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-200 hover:border-blue-200'
                    }`}
                  >
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Variety & Budget Section */}
        {activeSection === 'variety' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                <CurrencyPoundIcon className="h-4 w-4 inline mr-1" />
                Budget Level
              </label>
              <div className="grid grid-cols-3 gap-3">
                {([
                  { value: 'economy', label: 'Budget-Friendly', desc: 'Affordable ingredients' },
                  { value: 'moderate', label: 'Moderate', desc: 'Balanced approach' },
                  { value: 'premium', label: 'Premium', desc: 'Quality ingredients' },
                ] as const).map(option => (
                  <button
                    key={option.value}
                    onClick={() => setPreferences(prev => ({ ...prev, budget_level: option.value }))}
                    className={`px-4 py-3 rounded-lg border text-sm transition-all ${
                      preferences.budget_level === option.value
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-200 hover:border-blue-200'
                    }`}
                  >
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                <SparklesIcon className="h-4 w-4 inline mr-1" />
                Variety Preference
              </label>
              <div className="grid grid-cols-3 gap-3">
                {([
                  { value: 'low', label: 'Low', desc: 'Familiar favorites' },
                  { value: 'medium', label: 'Medium', desc: 'Some variety' },
                  { value: 'high', label: 'High', desc: 'Try new things' },
                ] as const).map(option => (
                  <button
                    key={option.value}
                    onClick={() => setPreferences(prev => ({ ...prev, variety_preference: option.value }))}
                    className={`px-4 py-3 rounded-lg border text-sm transition-all ${
                      preferences.variety_preference === option.value
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-200 hover:border-blue-200'
                    }`}
                  >
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.seasonal_preference}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    seasonal_preference: e.target.checked
                  }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">
                  Prefer seasonal ingredients
                </span>
              </label>
              <p className="ml-6 text-xs text-gray-500 mt-1">
                Use ingredients that are in season for better taste and value
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Your preferences are used when generating new meal plans
          </p>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              isSaved
                ? 'bg-green-600 text-white'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {saveMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Saving...
              </>
            ) : isSaved ? (
              <>
                <CheckIcon className="h-4 w-4" />
                Saved!
              </>
            ) : (
              'Save Preferences'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
